/* ThaBel — núcleo do cálculo de frete (usado pela página do produto e pelo carrinho)
   - Estado: descoberto pela faixa do CEP, no próprio tema; BrasilAPI e ViaCEP só de reserva, se o CEP estiver fora das faixas
   - Frenet: uma chamada direta à Shopify (cart/shipping_rates.json); se falhar, prepara e confere a cada 0,3 s, até duas vezes
   - Cache: guarda o resultado por 10 min (mesmo CEP + mesmo conteúdo) */
(() => {
  if (window.TbFrete) return;

  const CACHE_KEY = 'tb-frete-cache';
  const CACHE_MS = 10 * 60 * 1000;
  const espera = (ms) => new Promise((ok) => setTimeout(ok, ms));

  function comTempo(url, opcoes = {}, ms = 8000) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opcoes, signal: ctrl.signal }).finally(() => clearTimeout(t));
  }

  function lerCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function cacheObter(chave) {
    const c = lerCache()[chave];
    if (!c || Date.now() - c.t > CACHE_MS) return null;
    return c;
  }
  function cacheGuardar(chave, dados) {
    try {
      const tudo = lerCache();
      const agora = Date.now();
      Object.keys(tudo).forEach((k) => { if (agora - tudo[k].t > CACHE_MS) delete tudo[k]; });
      tudo[chave] = { ...dados, t: agora };
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(tudo));
    } catch (e) { /* sem armazenamento: tudo bem */ }
  }

  /* Faixas oficiais de CEP por estado (5 primeiros dígitos). */
  const FAIXAS = [
    [1000, 19999, 'SP'], [20000, 28999, 'RJ'], [29000, 29999, 'ES'], [30000, 39999, 'MG'],
    [40000, 48999, 'BA'], [49000, 49999, 'SE'], [50000, 56999, 'PE'], [57000, 57999, 'AL'],
    [58000, 58999, 'PB'], [59000, 59999, 'RN'], [60000, 63999, 'CE'], [64000, 64999, 'PI'],
    [65000, 65999, 'MA'], [66000, 68899, 'PA'], [68900, 68999, 'AP'], [69000, 69299, 'AM'],
    [69300, 69399, 'RR'], [69400, 69899, 'AM'], [69900, 69999, 'AC'], [70000, 72799, 'DF'],
    [72800, 72999, 'GO'], [73000, 73699, 'DF'], [73700, 76799, 'GO'], [76800, 76999, 'RO'],
    [77000, 77999, 'TO'], [78000, 78899, 'MT'], [79000, 79999, 'MS'], [80000, 87999, 'PR'],
    [88000, 89999, 'SC'], [90000, 99999, 'RS'],
  ];
  /* Devolve a sigla do estado pela tabela, ou null se o CEP estiver fora de todas as faixas. */
  function estadoDoCep(cep) {
    const n = parseInt(String(cep).replace(/\D/g, '').slice(0, 5), 10);
    const f = FAIXAS.find(([ini, fim]) => n >= ini && n <= fim);
    return f ? f[2] : null;
  }

  /* Reserva: só é usada se o CEP não estiver na tabela (ex.: faixa nova criada pelos Correios).
     Consulta BrasilAPI e ViaCEP ao mesmo tempo e usa a primeira que devolver o estado. */
  function estadoPelasApis(cep8) {
    const fontes = [
      comTempo(`https://brasilapi.com.br/api/cep/v1/${cep8}`, {}, 6000)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('api'))))
        .then((d) => (d && d.state ? String(d.state).toUpperCase() : Promise.reject(new Error('api')))),
      comTempo(`https://viacep.com.br/ws/${cep8}/json/`, {}, 6000)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('api'))))
        .then((d) => (d && !d.erro && d.uf ? String(d.uf).toUpperCase() : Promise.reject(new Error('api')))),
    ];
    return Promise.any(fontes).catch(() => { throw new Error('cep'); });
  }

  /* Tabela primeiro (instantâneo); APIs só de reserva. Lança 'cep' se ninguém reconhecer o CEP. */
  async function descobrirEstado(cep) {
    const cep8 = String(cep).replace(/\D/g, '');
    return estadoDoCep(cep8) || estadoPelasApis(cep8);
  }

  /* Caminho direto: uma única chamada que já espera a Frenet e devolve as taxas prontas.
     Devolve null se a Shopify não responder a tempo ou vier sem lista (aí usamos a reserva). */
  async function taxasDiretas(root, params) {
    try {
      const r = await comTempo(`${root}cart/shipping_rates.json?${params}`, { headers: { Accept: 'application/json' } }, 12000);
      if (r.status === 422) throw new Error('cep');
      if (!r.ok) return null;
      const d = (await r.json().catch(() => null)) || {};
      return Array.isArray(d.shipping_rates) && d.shipping_rates.length ? d.shipping_rates : null;
    } catch (e) {
      if (e && e.message === 'cep') throw e;
      return null;
    }
  }

  /* Reserva: pede à Shopify para preparar e confere até ficar pronto. Devolve a lista ou null se estourar o tempo. */
  async function pedirTaxas(root, params) {
    const prep = await comTempo(`${root}cart/prepare_shipping_rates.json?${params}`, { method: 'POST' }, 20000);
    if (prep.status === 422) throw new Error('cep');
    const inicio = Date.now();
    let pausa = 300;
    while (Date.now() - inicio < 25000) {
      await espera(pausa);
      const r = await comTempo(`${root}cart/async_shipping_rates.json?${params}`, {}, 12000);
      if (r.status === 422) throw new Error('cep');
      /* Enquanto a Frenet não responde, a Shopify devolve o texto "null" — trata como "ainda não", não como erro. */
      const d = (await r.json().catch(() => null)) || {};
      if (Array.isArray(d.shipping_rates)) return d.shipping_rates;
      pausa = Math.min(pausa + 200, 1000);
    }
    return null;
  }

  /* 1º o caminho direto; se falhar, a reserva (até duas vezes). Avisa "só mais um instante" se passar de 5 s. */
  async function taxas(root, cepFmt, aoDemorar, ufPronta) {
    const uf = ufPronta || await descobrirEstado(cepFmt);
    const params = new URLSearchParams({
      'shipping_address[zip]': cepFmt,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': uf,
    }).toString();
    const aviso = aoDemorar ? setTimeout(aoDemorar, 5000) : null;
    try {
      const direta = await taxasDiretas(root, params);
      if (direta) return direta;
      let ultimoErro = null;
      for (let tentativa = 0; tentativa < 2; tentativa += 1) {
        try {
          const lista = await pedirTaxas(root, params);
          if (lista) return lista;
          ultimoErro = new Error('tempo');
        } catch (e) {
          if (e && e.message === 'cep') throw e;
          ultimoErro = e;
        }
        await espera(400);
      }
      throw ultimoErro || new Error('tempo');
    } finally {
      if (aviso) clearTimeout(aviso);
    }
  }

  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  function htmlLista(lista) {
    const itens = lista.map((t) => {
      const preco = Number(t.price);
      const d = Array.isArray(t.delivery_days) ? t.delivery_days : [];
      const dias = d.length ? (d.length === 1 || d[0] === d[1] ? `Entrega em até ${d[d.length - 1]} dias úteis` : `Entrega de ${d[0]} a ${d[1]} dias úteis`) : '';
      const nome = String(t.name).replace(/[<>&"]/g, '');
      return `<li class="tb-cep__opcao"><span><strong>${nome}</strong>${dias ? `<span class="tb-suave">${dias}</span>` : ''}</span><span class="${preco === 0 ? 'tb-cep__gratis' : ''}">${preco === 0 ? 'Grátis' : moeda.format(preco)}</span></li>`;
    }).join('');
    return `<ul class="tb-cep__lista" role="list">${itens}</ul>`;
  }

  const MSG_CEP = '<p class="tb-cep__erro">CEP não encontrado. Confira os números.</p>';
  function mensagemErro(erro) {
    if (erro && erro.message === 'cep') return MSG_CEP;
    return '<p class="tb-cep__erro">Não foi possível calcular agora. <button type="button" class="tb-link-botao" data-tb-cep-tentar>Tentar de novo</button></p>';
  }

  window.TbFrete = { estadoDoCep, descobrirEstado, taxas, cacheObter, cacheGuardar, htmlLista, mensagemErro, MSG_CEP, comTempo, moeda };
})();

/* ThaBel — carrinho lateral e página do carrinho */
(() => {
  if (customElements.get('tb-carrinho')) return;

  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  /* fetch com limite de tempo: a gaveta nunca fica "carregando" para sempre */
  const buscar = (url, opcoes = {}, ms = 12000) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opcoes, signal: ctrl.signal }).finally(() => clearTimeout(t));
  };

  const todos = () => [...document.querySelectorAll('tb-carrinho')];
  const gaveta = () => document.querySelector('tb-carrinho[data-modo="gaveta"]');

  // Seções que precisam ser redesenhadas depois de qualquer mudança no carrinho
  const secoes = () => {
    const ids = todos().map((el) => el.dataset.section);
    const bolha = document.getElementById('cart-icon-bubble');
    if (bolha) ids.push(bolha.dataset.secao || 'cart-icon-bubble');
    return [...new Set(ids)].slice(0, 5);
  };

  const htmlDe = (html, seletor) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(seletor);
  };

  function aplicarSecoes(mapa) {
    if (!mapa) return;
    todos().forEach((el) => {
      const html = mapa[el.dataset.section];
      if (!html) return;
      const novo = htmlDe(html, '[data-tb-conteudo]');
      if (novo) el.trocarConteudo(novo.innerHTML);
    });
    const bolha = document.getElementById('cart-icon-bubble');
    const chave = bolha ? (bolha.dataset.secao || 'cart-icon-bubble') : null;
    if (bolha && mapa[chave]) {
      const novo = htmlDe(mapa[chave], '.shopify-section');
      if (novo) bolha.innerHTML = novo.innerHTML;
    }
    document.dispatchEvent(new CustomEvent('tb:carrinho-atualizado'));
  }

  async function redesenhar() {
    const root = (todos()[0] && todos()[0].dataset.root) || '/';
    const r = await buscar(`${root}?sections=${secoes().join(',')}`);
    if (r.ok) aplicarSecoes(await r.json());
  }

  async function chamar(rota, corpo) {
    const root = (todos()[0] && todos()[0].dataset.root) || '/';
    const payload = { ...corpo, sections: secoes().join(','), sections_url: window.location.pathname };
    const r = await buscar(`${root}cart/${rota}.js`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload) });
    const dados = await r.json().catch(() => ({}));
    if (!r.ok) {
      const erro = new Error(dados.description || dados.message || 'Não foi possível atualizar o carrinho.');
      erro.dados = dados;
      throw erro;
    }
    return dados;
  }

  class TbCarrinho extends HTMLElement {
    connectedCallback() {
      this.gaveta = this.dataset.modo === 'gaveta';
      this.ocupado = false;
      this.addEventListener('click', (e) => this.aoClicar(e));
      this.addEventListener('change', (e) => this.aoMudar(e));
      this.addEventListener('submit', (e) => this.aoEnviar(e));

      if (this.gaveta) {
        this.addEventListener('keydown', (e) => this.teclado(e));
        document.addEventListener('click', (e) => {
          const gatilho = e.target.closest('#cart-icon-bubble, [data-tb-abrir-carrinho]');
          if (!gatilho) return;
          e.preventDefault();
          this.abrir(gatilho);
        });
      }
      this.prepararCep();
    }

    /* ---------- abrir / fechar (gaveta) ---------- */
    abrir(gatilho) {
      if (!this.gaveta) return;
      this.gatilho = gatilho || document.activeElement;
      this.classList.add('is-aberto');
      document.documentElement.classList.add('tb-sem-rolagem');
      requestAnimationFrame(() => this.querySelector('.tb-gaveta__fechar')?.focus());
    }

    fechar() {
      this.classList.remove('is-aberto');
      document.documentElement.classList.remove('tb-sem-rolagem');
      if (this.gatilho && this.gatilho.focus) this.gatilho.focus();
    }

    teclado(e) {
      if (!this.classList.contains('is-aberto')) return;
      if (e.key === 'Escape') { this.fechar(); return; }
      if (e.key !== 'Tab') return;
      const focaveis = [...this.querySelectorAll('.tb-gaveta__painel a[href], .tb-gaveta__painel button:not([disabled]), .tb-gaveta__painel input, .tb-gaveta__painel select, .tb-gaveta__painel summary')]
        .filter((el) => el.offsetParent !== null);
      if (!focaveis.length) return;
      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      if (e.shiftKey && document.activeElement === primeiro) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primeiro.focus(); }
    }

    /* ---------- atualização do conteúdo ---------- */
    trocarConteudo(html) {
      const alvo = this.querySelector('[data-tb-conteudo]');
      if (!alvo) return;
      // guarda onde estava o foco para devolvê-lo depois
      const ativo = document.activeElement;
      let refoco = null;
      if (ativo && this.contains(ativo)) {
        const linha = ativo.closest('[data-tb-linha]');
        const papel = ['data-tb-qtd-linha', 'data-tb-trocar', 'data-tb-qtd-input', 'data-tb-remover'].find((a) => ativo.hasAttribute(a));
        if (linha && papel) refoco = { linha: linha.dataset.tbLinha, papel, valor: ativo.getAttribute(papel) };
      }
      const cupomAberto = this.querySelector('.tb-cupom')?.open;
      alvo.innerHTML = html;
      if (cupomAberto) { const c = this.querySelector('.tb-cupom'); if (c) c.open = true; }
      if (refoco) {
        const sel = `[data-tb-linha="${refoco.linha}"] [${refoco.papel}${refoco.valor ? `="${refoco.valor}"` : ''}]`;
        const el = this.querySelector(sel) || this.querySelector('.tb-gaveta__fechar');
        if (el) el.focus();
      } else if (this.gaveta && this.classList.contains('is-aberto') && !this.contains(document.activeElement)) {
        this.querySelector('.tb-gaveta__fechar')?.focus();
      }
      this.prepararCep();
    }

    anunciar(texto) {
      const el = this.querySelector('[data-tb-anuncio]');
      if (el) { el.textContent = ''; setTimeout(() => { el.textContent = texto; }, 50); }
    }

    async executar(fn, { linha } = {}) {
      if (this.ocupado) return;
      this.ocupado = true;
      this.classList.add('is-carregando');
      this.querySelectorAll('[data-tb-erro-linha]').forEach((e) => { e.hidden = true; });
      try {
        const dados = await fn();
        if (dados && dados.sections) aplicarSecoes(dados.sections);
        else await redesenhar();
        this.anunciar('Carrinho atualizado');
        return dados;
      } catch (erro) {
        await redesenhar().catch(() => {});
        const msg = erro.message || 'Não foi possível atualizar o carrinho.';
        const alvo = linha ? this.querySelector(`[data-tb-linha="${linha}"] [data-tb-erro-linha]`) : null;
        if (alvo) { alvo.textContent = msg; alvo.hidden = false; }
        this.anunciar(msg);
        throw erro;
      } finally {
        this.ocupado = false;
        this.classList.remove('is-carregando');
      }
    }

    /* ---------- eventos ---------- */
    aoClicar(e) {
      if (e.target.closest('[data-tb-fechar]')) { this.fechar(); return; }

      const linhaEl = e.target.closest('[data-tb-linha]');
      const remover = e.target.closest('[data-tb-remover]');
      if (remover && linhaEl) {
        this.executar(() => chamar('change', { line: Number(linhaEl.dataset.tbLinha), quantity: 0 })).catch(() => {});
        return;
      }
      const qtdBtn = e.target.closest('[data-tb-qtd-linha]');
      if (qtdBtn && linhaEl) {
        const input = linhaEl.querySelector('[data-tb-qtd-input]');
        const nova = Math.max(0, (parseInt(input.value, 10) || 0) + Number(qtdBtn.dataset.tbQtdLinha));
        const linha = Number(linhaEl.dataset.tbLinha);
        this.executar(() => chamar('change', { line: linha, quantity: nova }), { linha }).catch(() => {});
        return;
      }
      const add = e.target.closest('[data-tb-adicionar]');
      if (add) {
        this.executar(() => chamar('add', { items: [{ id: Number(add.dataset.tbAdicionar), quantity: 1 }] })).catch(() => {});
        return;
      }
      if (e.target.closest('[data-tb-cep-tentar]')) {
        const campo = this.querySelector('[data-tb-cep-form] input');
        if (campo) this.calcularFrete(campo.value);
        return;
      }
      const tirarCupom = e.target.closest('[data-tb-cupom-remover]');
      if (tirarCupom) {
        this.executar(() => chamar('update', { discount: '' })).catch(() => {});
      }
    }

    aoMudar(e) {
      const linhaEl = e.target.closest('[data-tb-linha]');
      if (!linhaEl) return;
      const linha = Number(linhaEl.dataset.tbLinha);

      if (e.target.matches('[data-tb-qtd-input]')) {
        const nova = Math.max(0, parseInt(e.target.value, 10) || 0);
        this.executar(() => chamar('change', { line: linha, quantity: nova }), { linha }).catch(() => {});
        return;
      }
      if (e.target.matches('[data-tb-trocar]')) {
        const novoId = Number(e.target.value);
        const chave = linhaEl.dataset.key;
        const qtd = parseInt(linhaEl.querySelector('[data-tb-qtd-input]').value, 10) || 1;
        this.executar(async () => {
          const root = this.dataset.root || '/';
          const r = await buscar(`${root}cart/add.js`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ items: [{ id: novoId, quantity: qtd }] }) });
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            throw new Error(d.description || 'Esse tamanho não tem estoque suficiente.');
          }
          return chamar('change', { id: chave, quantity: 0 });
        }, { linha }).catch(() => {});
      }
    }

    aoEnviar(e) {
      const cupomForm = e.target.closest('[data-tb-cupom-form]');
      if (cupomForm) {
        e.preventDefault();
        const codigo = cupomForm.querySelector('input').value.trim();
        if (codigo) this.aplicarCupom(codigo);
        return;
      }
      const cepForm = e.target.closest('[data-tb-cep-form]');
      if (cepForm) {
        e.preventDefault();
        this.calcularFrete(cepForm.querySelector('input').value);
      }
    }

    /* ---------- cupom ---------- */
    async aplicarCupom(codigo) {
      const mostrar = (texto, ok) => {
        const msg = this.querySelector('[data-tb-cupom-msg]');
        if (!msg) return;
        msg.textContent = texto;
        msg.hidden = false;
        msg.classList.toggle('is-erro', !ok);
      };
      // Um cupom por compra: guarda o que já estava valendo para devolver se o novo for inválido
      const anterior = this.querySelector('[data-tb-cupom-remover]')?.dataset.tbCupomRemover || '';
      if (anterior && anterior.toLowerCase() === codigo.toLowerCase()) { mostrar(`Cupom ${codigo.toUpperCase()} já está aplicado.`, true); return; }
      try {
        const dados = await this.executar(() => chamar('update', { discount: codigo }));
        if (dados && Array.isArray(dados.discount_codes)) {
          const achado = dados.discount_codes.find((d) => d.code.toLowerCase() === codigo.toLowerCase());
          if (achado && achado.applicable) {
            mostrar(`Cupom ${codigo.toUpperCase()} aplicado!`, true);
          } else {
            await this.executar(() => chamar('update', { discount: anterior })).catch(() => {});
            mostrar(anterior
              ? `Cupom inválido ou não vale para os produtos do carrinho. O cupom ${anterior.toUpperCase()} continua aplicado.`
              : 'Cupom inválido ou não vale para os produtos do carrinho.', false);
          }
        } else {
          // Loja sem suporte a cupom no carrinho: guarda o cupom para o checkout
          await fetch(`${this.dataset.root || '/'}discount/${encodeURIComponent(codigo)}`);
          mostrar(`Cupom ${codigo.toUpperCase()} salvo. O desconto aparece na finalização da compra.`, true);
        }
      } catch (erro) {
        mostrar('Não foi possível aplicar o cupom agora. Tente de novo na finalização.', false);
      }
    }

    /* ---------- frete por CEP (página do carrinho) ---------- */
    prepararCep() {
      const campo = this.querySelector('[data-tb-cep-form] input');
      if (!campo || campo.dataset.pronto) return;
      campo.dataset.pronto = '1';
      campo.addEventListener('input', () => {
        const d = campo.value.replace(/\D/g, '').slice(0, 8);
        campo.value = d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
      });
      let salvo = this.ultimoCep;
      if (!salvo) { try { salvo = localStorage.getItem('tb-cep'); } catch (e) { salvo = null; } }
      if (salvo) {
        campo.value = salvo;
        if (this.ultimoCep) this.calcularFrete(salvo);
      }
    }

    async calcularFrete(valor) {
      const saida = this.querySelector('[data-tb-cep-resultado]');
      const resumo = this.querySelector('[data-tb-frete-resumo]');
      if (!saida) return;
      const F = window.TbFrete;
      const cep = String(valor || '').replace(/\D/g, '');
      if (cep.length !== 8) { saida.innerHTML = '<p class="tb-cep__erro">Digite um CEP com 8 números.</p>'; return; }
      const cepFmt = `${cep.slice(0, 5)}-${cep.slice(5)}`;
      this.ultimoCep = cepFmt;
      try { localStorage.setItem('tb-cep', cepFmt); } catch (e) { /* sem armazenamento: tudo bem */ }
      if (!F) { saida.innerHTML = '<p class="tb-cep__erro">Não foi possível calcular agora. <button type="button" class="tb-link-botao" data-tb-cep-tentar>Tentar de novo</button></p>'; return; }
      const root = this.dataset.root || '/';
      const mostrar = (lista) => {
        saida.innerHTML = F.htmlLista(lista);
        if (resumo) {
          const menor = Math.min(...lista.map((t) => Number(t.price)));
          resumo.textContent = menor === 0 ? 'Grátis' : `a partir de ${moeda.format(menor)}`;
          resumo.classList.toggle('tb-cep__gratis', menor === 0);
        }
      };
      try {
        /* A chave do cache inclui o conteúdo do carrinho: mudou item ou quantidade, calcula de novo. */
        const carrinho = await buscar(`${root}cart.js`).then((r) => r.json());
        const assinatura = (carrinho.items || []).map((i) => `${i.variant_id}x${i.quantity}`).sort().join(',');
        const chave = `c:${assinatura}:${cep}`;
        const pronto = F.cacheObter(chave);
        if (pronto) { mostrar(pronto.lista); return; }
        saida.innerHTML = '<p class="tb-suave">Calculando…</p>';
        const uf = await F.descobrirEstado(cep);
        const lista = await F.taxas(root, cepFmt, () => {
          saida.innerHTML = '<p class="tb-suave">Consultando as transportadoras, só mais um instante…</p>';
        }, uf);
        if (!lista.length) { saida.innerHTML = F.MSG_CEP; return; }
        F.cacheGuardar(chave, { lista });
        mostrar(lista);
      } catch (erro) {
        console.warn('[ThaBel] frete:', erro);
        saida.innerHTML = F.mensagemErro(erro);
      }
    }

    /* ---------- chamado pela página de produto ---------- */
    async adicionarForm(form) {
      const fd = new FormData(form);
      fd.append('sections', secoes().join(','));
      fd.append('sections_url', window.location.pathname);
      const root = this.dataset.root || '/';
      const r = await buscar(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      const dados = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(dados.description || 'Não foi possível adicionar ao carrinho.');
      if (dados.sections) aplicarSecoes(dados.sections);
      else await redesenhar();
      if (this.dataset.abrirAoAdicionar !== 'false') this.abrir(form.querySelector('[type="submit"]'));
      this.anunciar('Produto adicionado ao carrinho');
      return dados;
    }
  }

  customElements.define('tb-carrinho', TbCarrinho);
  document.addEventListener('tb:carrinho-mudou', () => redesenhar().catch(() => {}));
  window.tbCarrinho = { gaveta, redesenhar };
})();
